'use client';
import React, { useState, useEffect } from 'react';
import { OrderReview as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { convertToLocale } from '@lib/util/money';
import { CART_UPDATED_EVENT } from '@/lib/hooks/useCart';
import { checkoutSelections, stepStatus } from './checkout-state';

const SharedRender = (Shared as any).render;

// Module-level state: the OrderReview component can get re-mounted when the
// Puck content tree re-renders (e.g. on cart refetch), which would reset
// local useState and look like state is "lost". Persisting at the module
// level means it survives re-mounts for the lifetime of the page.
// The SSR-safe initialization ensures the server always reports the
// fresh-state default (matching the default props of the shared component).
const moduleState: { agreed: boolean; isProcessing: boolean; whatsappOptIn: boolean } = {
  agreed: false,
  isProcessing: false,
  whatsappOptIn: true,
};

const mapAddress = (a: any) => {
  if (!a) return undefined;
  return {
    name: [a.first_name, a.last_name].filter(Boolean).join(' '),
    street: [a.address_1, a.address_2].filter(Boolean).join(', '),
    city: a.city ?? '',
    state: a.province ?? '',
    zip: a.postal_code ?? '',
    country: (a.country_code ?? '').toUpperCase(),
    email: '',
  };
};

/**
 * Storefront OrderReview — pulls shipping address (from cart), shipping
 * method (selected), and payment method (selected) from Puck context
 * and maps to the shared component's shape.
 *
 * On Place Order: POSTs the shipping address to /api/cart/address
 * (shipping + payment methods are already attached via the wrapper's
 * own on-click handlers in ShippingMethod / PaymentMethod), then POSTs
 * /api/cart/complete to place the order, then redirects to the
 * confirmation page.
 */
function OrderReviewImpl(props: any) {
  const { context } = usePuckContext() as any;
  const cart = context?.cart;
  const customer = context?.customer;
  // The country code for the redirect URL. Priority:
  //   1. Puck context (injected by checkout/page.tsx from URL params)
  //   2. URL's first path segment (the [countryCode] from the route)
  // We deliberately do NOT fall back to a hardcoded 'us' — the user's
  // region is the only correct value for the redirect URL. Using a
  // hardcoded fallback here caused the middleware to prepend the
  // actual region (e.g. /us → /dk/us → 404).
  const countryCode: string = (() => {
    if (context?.countryCode) return String(context.countryCode).toLowerCase();
    if (typeof window !== 'undefined') {
      return window.location.pathname.split('/')[1]?.toLowerCase() || '';
    }
    return '';
  })();

  // === All hooks must be called in the same order on every render. ===

  // Re-render trigger: Puck context is SSR-frozen, so without
  // listening to CART_UPDATED_EVENT the wrapper never re-evaluates
  // the step-gating after PaymentMethod initiates a session. Bumping
  // `trigger` forces a fresh render so the gate sees the new
  // `checkoutSelections.paymentId`.
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const onCartUpdated = () => setTrigger((t) => t + 1);
    window.addEventListener(CART_UPDATED_EVENT, onCartUpdated);
    return () => window.removeEventListener(CART_UPDATED_EVENT, onCartUpdated);
  }, []);

  // Local UI state — initialised from module-level state so the
  // values survive any re-mounts of the wrapper.
  const [isProcessing, setIsProcessingLocal] = useState(moduleState.isProcessing);
  const setIsProcessing = (v: boolean) => {
    moduleState.isProcessing = v;
    setIsProcessingLocal(v);
  };
  const [agreed, setAgreedLocal] = useState(moduleState.agreed);
  const [whatsappOptIn, setWhatsappOptInLocal] = useState(moduleState.whatsappOptIn);
  const setAgreed = (v: boolean) => {
    moduleState.agreed = v;
    setAgreedLocal(v);
  };
  const setWhatsappOptIn = (v: boolean) => {
    setWhatsappOptInLocal(v);
    moduleState.whatsappOptIn = v;
  };
  const [error, setError] = useState<string | null>(null);

  // === Conditional returns ONLY come after all hooks above. ===

  // Step 4 of the progressive step-by-step checkout: only render once
  // the address, shipping method, AND a pending payment session are all
  // saved. Cumulatively checking the earlier steps prevents the
  // section from showing when the cart has a stale pending session
  // (e.g. from a previous test) but the address or shipping is empty.
  // Cart from Puck context may be stale; fall back to
  // `checkoutSelections.{address,shippingId,paymentId}` for instant
  // appearance after the user just saved those steps.
  if (!cart) return null;
  if (
    !stepStatus.isReviewAccessible(
      cart,
      !!checkoutSelections.address,
      !!checkoutSelections.shippingId,
      !!checkoutSelections.paymentId,
    )
  ) {
    return null;
  }
  void trigger;

  if (!cart) {
    return <SharedRender {...props} />;
  }

  // Prefer the staging address (saved on Continue); fall back to the
  // Puck context (the cart's address, which may be stale until the
  // page re-renders).
  const addr = (() => {
    const src = checkoutSelections.address || cart.shipping_address;
    const mapped = mapAddress(src);
    if (mapped && customer?.email) mapped.email = customer.email;
    return mapped;
  })();

  // Prefer the user's most recent pick (from module state, set by the
  // ShippingMethod / PaymentMethod wrappers on click). Fall back to the
  // Puck context (which is the SSR-rendered cart, may be stale).
  const selectedShipping = (cart.shipping_methods ?? [])[0];
  const ship = (() => {
    if (checkoutSelections.shippingId) {
      return {
        name: checkoutSelections.shippingName,
        price: checkoutSelections.shippingPrice,
        estimate: checkoutSelections.shippingEstimate,
      };
    }
    if (!selectedShipping) return undefined;
    return {
      name: selectedShipping.name ?? selectedShipping.shipping_option?.name ?? 'Shipping',
      price: convertToLocale({
        amount: Number(selectedShipping.amount) || 0,
        currency_code: cart.currency_code || 'usd',
      }),
      estimate: selectedShipping.shipping_option?.data?.eta_days
        ? `${selectedShipping.shipping_option.data.eta_days} business days`
        : 'Calculated at checkout',
    };
  })();

  // Read the active payment session from the cart (or the staging from PaymentMethod wrapper)
  const paySession = (cart.payment_collection?.payment_sessions ?? []).find(
    (s: any) => s.status === 'pending',
  );
  const pay = (() => {
    if (checkoutSelections.paymentId) {
      return { description: checkoutSelections.paymentDescription };
    }
    if (!paySession) return undefined;
    return {
      description: paySession.provider_id
        ? paySession.provider_id.replace(/^pp_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : 'Selected payment method',
    };
  })();

  const onPlaceOrder = async () => {
    setError(null);
    if (isProcessing) return;
    if (!cart.shipping_address && !checkoutSelections.address) {
      setError('Please complete the shipping address first.');
      return;
    }
    // Accept both the cart's server-side selection AND the staging
    // value (in case the cart hasn't refetched yet in Puck context).
    if (!ship && !checkoutSelections.shippingId) {
      setError('Please select a shipping method.');
      return;
    }
    if (!pay && !checkoutSelections.paymentId) {
      setError('Please select a payment method.');
      return;
    }
    setIsProcessing(true);
    try {
      // The address was already saved in step 1 (ShippingAddress →
      // "Continue to Shipping Method"). Re-saving it here is NOT safe
      // because Medusa rejects address updates once a payment session
      // has been initiated (it returns 500). Just complete the cart.
      const completeRes = await fetch('/api/cart/complete', { method: 'POST' });
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to place order');
      }
      const completeData = await completeRes.json();
      const orderId: string | undefined = completeData?.orderId;
      if (!orderId) throw new Error('Order placed but no orderId returned');

      // WhatsApp opt-in capture — fire-and-forget, never blocks the redirect.
      // Uses the shipping phone already on the cart; the backend dedupes
      // (upsert by storeId+phone) and enforces the opt-out list.
      try {
        const storeId = typeof document !== 'undefined' ? document.body?.dataset?.storeId : undefined;
        const phone = (cart as any)?.shipping_address?.phone;
        if (storeId && phone && moduleState.whatsappOptIn) {
          const apiBase = process.env.NEXT_PUBLIC_BACKEND_URL ?? '';
          void fetch(`${apiBase}/whatsapp/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ storeId, phoneNumber: phone, optedIn: true, source: 'checkout' }),
          }).catch(() => {});
        }
      } catch { /* non-critical */ }

      // Use the user's REGION (from Puck context, which is the URL's
      // `[countryCode]` param) for the redirect URL — NOT the order's
      // shipping country. The order's country (from the API response)
      // can differ from the user's region (e.g. a Danish shopper
      // using a US cart). If we used the order's country, the
      // middleware would see `/us/...` and prepend the user's region
      // → `/dk/us/...` → 404. The confirmation page works fine at
      // the user's region URL — it reads the order by id, not by
      // country.
      const finalCountryCode: string = countryCode;

      // Redirect to the success page
      window.location.href = `/${finalCountryCode}/order/${orderId}/confirmed`;
    } catch (e: any) {
      setError(e?.message || 'Failed to place order');
      setIsProcessing(false);
    }
  };

  return (
    <SharedRender
      {...props}
      shippingAddress={addr}
      shippingMethod={ship}
      paymentMethod={pay}
      onPlaceOrder={onPlaceOrder}
      isProcessing={isProcessing}
      agreedToTerms={agreed}
      onAgreedChange={setAgreed}
      whatsappOptIn={whatsappOptIn}
      onWhatsappOptInChange={setWhatsappOptIn}
    />
  );
}

export const OrderReview: any = {
  ...Shared,
  render: (props: any) => <OrderReviewImpl {...props} />,
};
export default OrderReview;
